import React from "react";

/**
 * Unified status manager.
 *
 * Color is resolved id-first: the app-wide status id scheme (1-15, or "A"/"I")
 * drives the color. The status name is only used as a fallback when no usable
 * id is supplied. Pass a `statusId` and a `statusName` and get a consistently
 * colored pill.
 */

const BUCKET_CLASS = {
  success: "bg-green-100 text-green-700",
  warning: "bg-yellow-100 text-yellow-700",
  info: "bg-blue-100 text-blue-700",
  danger: "bg-red-100 text-red-700",
  neutral: "bg-gray-100 text-gray-600",
  default: "bg-gray-100 text-gray-700",
};

// Normalized (lowercased, trimmed) status name -> semantic bucket.
const NAME_BUCKET = {
  // success (green)
  active: "success",
  approved: "success",
  paid: "success",
  success: "success",
  completed: "success",
  done: "success",
  verified: "success",
  published: "success",
  enabled: "success",
  // warning (yellow)
  draft: "warning",
  pending: "warning",
  "pending for approval": "warning",
  "awaiting approval": "warning",
  processing: "warning",
  "on hold": "warning",
  // info (blue)
  submitted: "info",
  "in transit": "info",
  "in progress": "info",
  "partially paid": "info",
  // danger (red)
  rejected: "danger",
  cancelled: "danger",
  canceled: "danger",
  void: "danger",
  failed: "danger",
  declined: "danger",
  overdue: "danger",
  expired: "danger",
  // neutral / inactive (gray)
  inactive: "neutral",
  closed: "neutral",
  disabled: "neutral",
  archived: "neutral",
};

// Letter ids — resolved first.
const ID_BUCKET = {
  A: "success",
  I: "neutral",
};

// Numeric-id palette (1-15) — the app-wide status scheme, resolved first.
// Full class literals are kept here so Tailwind's scanner never purges them.
const NUMERIC_ID_CLASS = {
  1: "bg-green-100 text-green-700",
  2: "bg-blue-100 text-blue-700",
  3: "bg-yellow-100 text-yellow-700",
  4: "bg-red-100 text-red-700",
  5: "bg-purple-100 text-purple-700",
  6: "bg-orange-100 text-orange-700",
  7: "bg-teal-100 text-teal-700",
  8: "bg-pink-100 text-pink-700",
  9: "bg-indigo-100 text-indigo-700",
  10: "bg-cyan-100 text-cyan-700",
  11: "bg-lime-100 text-lime-700",
  12: "bg-amber-100 text-amber-700",
  13: "bg-rose-100 text-rose-700",
  14: "bg-sky-100 text-sky-700",
  15: "bg-fuchsia-100 text-fuchsia-700",
};

/**
 * Returns the Tailwind `bg-… text-…` classes for a given status.
 * @param {string} [statusName]
 * @param {number|string} [statusId]
 */
export function getStatusColorClass(statusName, statusId) {
  const rawId = String(statusId ?? "").trim();
  const idBucket = ID_BUCKET[rawId.toUpperCase()];
  if (idBucket) return BUCKET_CLASS[idBucket];

  const numericClass = NUMERIC_ID_CLASS[Number(rawId)];
  if (rawId !== "" && numericClass) return numericClass;

  // Name fallback — only when no usable id was supplied.
  const normalized = String(statusName ?? "").trim().toLowerCase();
  const nameBucket = normalized ? NAME_BUCKET[normalized] : undefined;
  if (nameBucket) return BUCKET_CLASS[nameBucket];

  return BUCKET_CLASS.default;
}

const SIZE_CLASS = {
  sm: "px-2.5 py-0.5 text-xs",
  md: "px-4 py-2 text-sm",
};

/**
 * StatusBadge — renders a unified, colored status pill.
 * @param {object} props
 * @param {number|string} [props.statusId]  1-15 or "A"/"I"
 * @param {string} [props.statusName]        display label
 * @param {"sm"|"md"} [props.size]           default "sm"
 * @param {string} [props.className]         extra classes appended
 */
export default function StatusBadge({
  statusId,
  statusName,
  size = "sm",
  className = "",
}) {
  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.sm;
  const colorClass = getStatusColorClass(statusName, statusId);

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${sizeClass} ${colorClass} ${className}`.trim()}
    >
      {statusName ?? "—"}
    </span>
  );
}
